/**
 * Top-down diagram of where the phone goes.
 *
 * A written instruction ("place the phone to your side and behind you") is
 * reliably misread; a picture is not. This is a plain SVG in the app's own
 * palette — no illustration library, no icon font, no emoji.
 *
 * What the placement has to achieve, and what the diagram must therefore show:
 * the phone sits to one side and slightly behind, high enough to see the desk
 * surface, both hands, the edge of the laptop screen, and the student.
 */
export function PlacementDiagram({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 320 200"
      role="img"
      aria-label="Top-down view of a desk. The phone stands to the right and slightly behind the student, angled across the desk so it sees the desk surface, both hands, the edge of the laptop screen and the student."
      className={className}
    >
      {/* Desk */}
      <rect
        x="60"
        y="40"
        width="150"
        height="90"
        rx="4"
        fill="var(--color-surface-sunken)"
        stroke="var(--color-border-strong)"
      />
      <text
        x="70"
        y="56"
        fontSize="9"
        fill="var(--color-ink-faint)"
        fontFamily="var(--font-sans)"
      >
        Desk
      </text>

      {/* Laptop */}
      <rect
        x="95"
        y="52"
        width="70"
        height="34"
        rx="3"
        fill="var(--color-surface)"
        stroke="var(--color-ink-muted)"
      />
      <text
        x="106"
        y="73"
        fontSize="9"
        fill="var(--color-ink-muted)"
        fontFamily="var(--font-sans)"
      >
        Laptop
      </text>

      {/* Rough sheets */}
      <rect
        x="95"
        y="94"
        width="42"
        height="26"
        rx="2"
        fill="var(--color-surface)"
        stroke="var(--color-border-strong)"
      />
      <text
        x="99"
        y="110"
        fontSize="8"
        fill="var(--color-ink-faint)"
        fontFamily="var(--font-sans)"
      >
        Sheets
      </text>

      {/* Student */}
      <circle
        cx="135"
        cy="160"
        r="15"
        fill="var(--color-surface)"
        stroke="var(--color-ink-muted)"
      />
      <text
        x="118"
        y="192"
        fontSize="9"
        fill="var(--color-ink-muted)"
        fontFamily="var(--font-sans)"
      >
        You
      </text>

      {/* Field of view */}
      <path
        d="M262 138 L120 44 L86 128 Z"
        fill="var(--color-accent)"
        fillOpacity="0.07"
        stroke="var(--color-accent)"
        strokeOpacity="0.35"
        strokeDasharray="3 3"
      />

      {/* Phone */}
      <rect
        x="254"
        y="126"
        width="16"
        height="28"
        rx="3"
        fill="var(--color-surface)"
        stroke="var(--color-accent)"
        strokeWidth="1.5"
      />
      <circle cx="262" cy="133" r="1.8" fill="var(--color-accent)" />
      <text
        x="238"
        y="172"
        fontSize="9"
        fill="var(--color-accent)"
        fontFamily="var(--font-sans)"
        fontWeight="500"
      >
        Phone
      </text>
    </svg>
  );
}
